import { useCallback, useEffect } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'danger' | 'primary';
  icon?: string;
  loading?: boolean;
}

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = '¿Estás seguro?',
  message = 'Esta acción no se puede deshacer.',
  confirmText = 'Sí, confirmar',
  cancelText = 'Cancelar',
  confirmColor = 'danger',
  icon = 'ri-alert-line',
  loading = false,
}: ConfirmDialogProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  const confirmBg =
    confirmColor === 'danger'
      ? 'bg-[#DC2626] hover:bg-[#B91C1C]'
      : 'bg-[#2563EB] hover:bg-[#1E40AF]';

  const iconBg =
    confirmColor === 'danger'
      ? 'bg-[#DC2626]/10 text-[#DC2626]'
      : 'bg-[#2563EB]/10 text-[#2563EB]';

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-50 animate-fade-in"
        onClick={() => !loading && onClose()}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                <i className={`${icon} w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#111827]">{title}</h3>
                <p className="text-sm text-gray-500">Esta acción no se puede deshacer</p>
              </div>
            </div>

            <div className="bg-[#F4F6F8] rounded-xl p-4">
              <p className="text-sm text-gray-700">{message}</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-2.5 bg-[#F4F6F8] text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={loading}
                className={`flex-1 py-2.5 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${confirmBg}`}
              >
                {loading ? (
                  <>
                    <div className="spinner w-4 h-4 border-2 border-white border-t-transparent"></div>
                    <span>Procesando...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{confirmText}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}